"use client";

import { c as _c } from "react/compiler-runtime";
import { IconListBullets, IconMagnifyingGlass } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconChevronDownOutline18, IconChevronLeftOutline18, IconChevronRightOutline18, IconSlidersOutline18, IconSquareLayoutGrid2Outline18 } from "nucleo-ui-outline-18";
import { ConnectorsListSkeleton } from "@/app/(v2)/_components/PageSkeletons";
import ConfirmModal from "@/components/custom/ConfirmModal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import type { Tab } from "@/components/ui/filterable-tab-layout";
import { EmptyState } from "@/components/ui/interactive-empty-state";
import { PageLayout } from "@/components/ui/page-layout";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DEFAULT_PAGE_SIZE } from "@/constants/pagination";
import { EASE_OUT_QUART } from "@/lib/animation-constants";
import { connectorTypeToIntegrationSlug } from "@/lib/connectors/slug";
import { showToast } from "@/lib/toast";
import type { Connector, ConnectorType } from "@/stores/connectorsStore";
import { useConnectorsStore } from "@/stores/connectorsStore";
import { ExchangeIcon, WalletIcon } from "@web3icons/react/dynamic";
import { motion } from "motion/react";
import { useRouter, useSearchParams } from "next/navigation";
import { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { AvailableConnectorRow } from "./AvailableConnectorRow";
import { ConfiguredConnectorRow } from "./ConfiguredConnectorRow";
import { ConnectorCard } from "./ConnectorCard";
import ConnectorFormModal from "./ConnectorFormModal";
import { ConnectorLogo } from "./ConnectorLogo";
import { PipedreamConnectorModal } from "./PipedreamConnectorModal";
import { usePipedreamConnect } from "./usePipedreamConnect";
const connectorTabs: Tab[] = [{
  id: "available",
  label: "Available Connectors"
}, {
  id: "configured",
  label: "My Connectors"
}];
interface ConfiguredConnectorSection {
  type: string;
  label: string;
  count: number;
  latestTimestamp: number;
  connectorType?: ConnectorType;
  connectors: Connector[];
}
function formatConnectorTypeLabel(type: string, connectorType?: ConnectorType): string {
  if (connectorType?.name) return connectorType.name;
  const normalizedType = type.includes(":") ? type.split(":")[1] : type;
  return normalizedType.replace(/[-_]/g, " ").replace(/\b\w/g, char => char.toUpperCase());
}
function getConnectorTimestamp(connector: Connector): number {
  return new Date(connector.updatedAt || connector.createdAt).getTime();
}
function ConnectorsPageContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const connectors = useConnectorsStore(state => state.connectors);
  const connectorTypes = useConnectorsStore(state => state.connectorTypes);
  const loading = useConnectorsStore(state => state.loading);
  const lastLoadedAt = useConnectorsStore(state => state.lastLoadedAt);
  const loadConnectors = useConnectorsStore(state => state.loadConnectors);
  const loadConnectorTypes = useConnectorsStore(state => state.loadConnectorTypes);
  const deleteConnector = useConnectorsStore(state => state.deleteConnector);
  const storeActiveTab = useConnectorsStore(state => state.activeTab);
  const setStoreActiveTab = useConnectorsStore(state => state.setActiveTab);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedAvailableConnectorType, setSelectedAvailableConnectorType] = useState<string | undefined>(undefined);
  const [editingConnector, setEditingConnector] = useState<Connector | null>(null);
  const [creatingConnectorType, setCreatingConnectorType] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [connectorToDelete, setConnectorToDelete] = useState<string | null>(null);
  const [pages, setPages] = useState({
    available: 0,
    configured: 0
  });
  const {
    isConnecting: isPipedreamConnecting,
    connectingType,
    pendingConnection,
    connect: connectPipedream,
    clearPendingConnection
  } = usePipedreamConnect();
  const urlTab = searchParams.get("tab");
  const defaultTab = !loading && lastLoadedAt !== null && connectors.length === 0 ? "available" : "configured";
  const activeTab = (urlTab === "available" || urlTab === "configured" ? urlTab : null) || storeActiveTab || defaultTab;
  const setActiveTab = useCallback((tab: string) => {
    setShowFilters(false);
    setStoreActiveTab(tab as "available" | "configured");
    const params = new URLSearchParams(searchParams.toString());
    params.set("tab", tab);
    router.replace(`/connectors?${params.toString()}`);
  }, [searchParams, router, setStoreActiveTab]);
  const currentTabKey = (activeTab || "configured") as "available" | "configured";
  const page = pages[currentTabKey];
  const setPage = useCallback((updater: number | ((p: number) => number)) => {
    setPages(prev => ({
      ...prev,
      [currentTabKey]: typeof updater === "function" ? updater(prev[currentTabKey]) : updater
    }));
  }, [currentTabKey]);
  useEffect(() => {
    loadConnectorTypes();
    loadConnectors();
  }, [loadConnectorTypes, loadConnectors]);

  // Handle Slack OAuth redirect feedback
  useEffect(() => {
    const slackSuccess = searchParams.get("slack_success");
    const slackError = searchParams.get("slack_error");
    const cleanupUrlParam = (paramToDelete: string) => {
      const params = new URLSearchParams(searchParams.toString());
      params.delete(paramToDelete);
      const newUrl = params.toString() ? `/connectors?${params.toString()}` : "/connectors";
      router.replace(newUrl);
    };
    if (slackSuccess) {
      showToast.success(`Slack workspace connected: ${slackSuccess}`);
      const params = new URLSearchParams(searchParams.toString());
      params.delete("slack_success");
      params.set("tab", "configured");
      router.replace(`/connectors?${params.toString()}`);
      setStoreActiveTab("configured");
    } else if (slackError) {
      showToast.error(slackError);
      cleanupUrlParam("slack_error");
    }
  }, [searchParams, router, setStoreActiveTab]);
  const handleDelete = (id: string) => {
    setConnectorToDelete(id);
  };
  const confirmDelete = async () => {
    if (!connectorToDelete) return;
    setDeletingId(connectorToDelete);
    try {
      await deleteConnector(connectorToDelete);
      showToast.success("Connector deleted successfully");
    } catch (error) {
      showToast.error(error instanceof Error ? error.message : "Failed to delete connector");
    } finally {
      setDeletingId(null);
      setConnectorToDelete(null);
    }
  };
  const handleEdit = (connector: Connector) => {
    setEditingConnector(connector);
  };
  const handleCloseModal = () => {
    setCreatingConnectorType(null);
    setEditingConnector(null);
  };
  const handleAddConnector = (type: string) => {
    const connectorType = connectorTypes.find(ct => ct.type === type);
    if (connectorType?.provider === "pipedream") {
      connectPipedream(connectorType);
    } else {
      setCreatingConnectorType(type);
    }
  };

  // Auto-open Connect modal when arriving via /connectors?connect={integrationSlug}
  const connectRequest = searchParams.get("connect");
  useEffect(() => {
    if (!connectRequest || connectorTypes.length === 0) return;
    const match = connectorTypes.find(ct => connectorTypeToIntegrationSlug(ct.type) === connectRequest.toLowerCase());
    const params = new URLSearchParams(searchParams.toString());
    params.delete("connect");
    router.replace(params.toString() ? `/connectors?${params.toString()}` : "/connectors");
    if (!match) {
      showToast.error(`Connector "${connectRequest}" is not available`);
      return;
    }
    if (match.provider === "pipedream") {
      connectPipedream(match);
    } else {
      setCreatingConnectorType(match.type);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectRequest, connectorTypes]);
  const connectorTypeMap = useMemo(() => new Map(connectorTypes.map(connectorType => [connectorType.type, connectorType])), [connectorTypes]);
  const connectedTypes = useMemo(() => new Set(connectors.map(c => c.type)), [connectors]);
  const filteredConnectorTypes = useMemo(() => {
    let filtered = [...connectorTypes];
    if (selectedAvailableConnectorType) {
      filtered = filtered.filter(ct => ct.type === selectedAvailableConnectorType);
    }
    if (searchTerm) {
      const normalizedSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(ct => ct.name.toLowerCase().includes(normalizedSearch) || ct.type.toLowerCase().includes(normalizedSearch) || ct.description?.toLowerCase().includes(normalizedSearch));
    }
    return filtered;
  }, [connectorTypes, selectedAvailableConnectorType, searchTerm]);
  const filteredConnectors = useMemo(() => {
    let filtered = connectors;
    if (searchTerm) {
      const normalizedSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(c => c.name.toLowerCase().includes(normalizedSearch) || c.type.toLowerCase().includes(normalizedSearch) || c.description?.toLowerCase().includes(normalizedSearch) || formatConnectorTypeLabel(c.type, connectorTypeMap.get(c.type)).toLowerCase().includes(normalizedSearch));
    }
    return filtered;
  }, [connectors, connectorTypeMap, searchTerm]);
  const paginatedConnectorTypes = useMemo(() => {
    const start = page * DEFAULT_PAGE_SIZE;
    return filteredConnectorTypes.slice(start, start + DEFAULT_PAGE_SIZE);
  }, [filteredConnectorTypes, page]);
  const hasMore = useMemo(() => {
    const items = activeTab === "available" ? filteredConnectorTypes : filteredConnectors;
    return (page + 1) * DEFAULT_PAGE_SIZE < items.length;
  }, [activeTab, filteredConnectorTypes, filteredConnectors, page]);
  useEffect(() => {
    setPages({
      available: 0,
      configured: 0
    });
  }, [searchTerm, selectedAvailableConnectorType]);
  const configuredSections = useMemo<ConfiguredConnectorSection[]>(() => {
    const sectionsMap = new Map<string, ConfiguredConnectorSection>();
    filteredConnectors.forEach(connector => {
      const connectorType = connectorTypeMap.get(connector.type);
      const label = formatConnectorTypeLabel(connector.type, connectorType);
      const timestamp = getConnectorTimestamp(connector);
      const existing = sectionsMap.get(connector.type);
      if (existing) {
        existing.count += 1;
        existing.latestTimestamp = Math.max(existing.latestTimestamp, timestamp);
        existing.connectors.push(connector);
        return;
      }
      sectionsMap.set(connector.type, {
        type: connector.type,
        label,
        count: 1,
        latestTimestamp: timestamp,
        connectorType,
        connectors: [connector]
      });
    });
    const sections = Array.from(sectionsMap.values());
    sections.forEach(section => {
      section.connectors.sort((a, b) => getConnectorTimestamp(b) - getConnectorTimestamp(a));
    });
    sections.sort((a, b) => {
      if (b.count !== a.count) {
        return b.count - a.count;
      }
      return a.label.localeCompare(b.label);
    });
    return sections;
  }, [connectorTypeMap, filteredConnectors]);
  const hasActiveAvailableFilters = Boolean(searchTerm || selectedAvailableConnectorType);
  const hasActiveConfiguredFilters = Boolean(searchTerm);
  const handleClearAvailableFilters = useCallback(() => {
    setSearchTerm("");
    setSelectedAvailableConnectorType(undefined);
  }, []);
  const handleClearConfiguredFilters = useCallback(() => {
    setSearchTerm("");
  }, []);
  return <PageLayout>
      {/* Page Title */}
      <div className="mb-6">
        <h2 className="text-ds-text-secondary text-xl font-medium dark:text-zinc-200">Connectors</h2>
        <p className="body-sm mt-0.5">Supercharge your workflow and connect the tools you use every day.</p>
      </div>
      {/* Combined filter bar: pill tabs + controls */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        {/* Pill tabs — shared Tabs component */}
        <Tabs value={activeTab} onValueChange={value => setActiveTab(value as (typeof connectorTabs)[number]["id"])} variant="default" size="md">
          <TabsList data-guide="connectors-tabs">
            {connectorTabs.map(tab => <TabsTrigger key={tab.id} value={tab.id}>
                {tab.label}
              </TabsTrigger>)}
          </TabsList>
        </Tabs>

        {/* Right-side controls */}
        <div className="ml-auto flex items-center gap-3">
          {/* Grid/List toggle — only meaningful on the Available tab. The
              Configured tab renders an Accordion grouped by connector type
              regardless of viewMode, so showing a non-functional toggle there
              looks broken. */}
          {activeTab === "available" && <Tabs value={viewMode} onValueChange={value => setViewMode(value as typeof viewMode)} variant="default">
              <TabsList aria-label="View mode">
                <TabsTrigger value="grid" aria-label="Grid view" className="size-9 px-0">
                  <IconSquareLayoutGrid2Outline18 className="size-4" />
                </TabsTrigger>
                <TabsTrigger value="list" aria-label="List view" className="size-9 px-0">
                  <IconListBullets className="size-4" />
                </TabsTrigger>
              </TabsList>
            </Tabs>}

          {/* Inline search */}
          <div className="relative">
            <IconMagnifyingGlass className="text-ds-text-disabled absolute left-2.5 top-1/2 size-4 -translate-y-1/2" />
            <input type="text" placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="ds-soft-input text-ds-text-secondary placeholder:text-ds-text-disabled dark:placeholder:text-ds-text-muted h-11 w-[200px] pl-9 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30" data-guide="connectors-search" />
            <kbd className="ds-soft-kbd pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 px-1 py-0.5">
              ⌘/
            </kbd>
          </div>

          {/* Filters button */}
          <button type="button" onClick={() => setShowFilters(!showFilters)} className="ds-skeuomorphic-btn hover:to-zinc-150 rounded-ds-sm shadow-ds-white from-ds-neutral-100 to-ds-neutral-200 text-ds-text-primary dark:from-ds-neutral-700 dark:to-ds-neutral-800 flex h-11 items-center gap-1.5 bg-gradient-to-b px-3 text-sm font-medium transition-all hover:from-zinc-50 active:scale-[0.98] dark:text-zinc-200">
            <IconSlidersOutline18 className="size-4" />
            Filters
            <IconChevronDownOutline18 className="size-4" />
          </button>
        </div>
      </div>
      {/* Loading State */}
      {loading && connectorTypes.length === 0 && <div className="animate-in fade-in duration-300">
          <ConnectorsListSkeleton count={6} />
        </div>}
      {/* Content */}
      {!loading && activeTab && <div className="animate-in fade-in mb-4 duration-300">
          {activeTab === "available" ? (/* Available Connectors */
      <>
              {filteredConnectorTypes.length > 0 ? <>
                  {viewMode === "grid" ? <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                      {paginatedConnectorTypes.map((connectorType, idx) => <motion.div key={connectorType.type} initial={{
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: Math.min(idx, 7) * 0.04,
              ease: EASE_OUT_QUART
            }}>
                          <ConnectorCard connectorType={connectorType} onAdd={handleAddConnector} isConnecting={isPipedreamConnecting && connectingType === connectorType.type} isConnected={connectedTypes.has(connectorType.type)} onManage={() => setActiveTab("configured")} />
                        </motion.div>)}
                    </div> : <div>
                      {paginatedConnectorTypes.map(connectorType => <AvailableConnectorRow key={connectorType.type} connectorType={connectorType} onAdd={handleAddConnector} isConnecting={isPipedreamConnecting && connectingType === connectorType.type} isConnected={connectedTypes.has(connectorType.type)} onManage={() => setActiveTab("configured")} />)}
                    </div>}

                  {/* Pagination */}
                  {(page > 0 || hasMore) && <div className="mt-4 flex items-center justify-between">
                      <p className="text-ds-text-muted text-sm">
                        Showing {Math.min(page * DEFAULT_PAGE_SIZE + 1, filteredConnectorTypes.length)} -{" "}
                        {page * DEFAULT_PAGE_SIZE + paginatedConnectorTypes.length} of {filteredConnectorTypes.length}{" "}
                        connector types
                      </p>
                      <nav className="flex gap-2" aria-label="Pagination">
                        <Button variant="white" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 0} aria-label="Previous page">
                          <IconChevronLeftOutline18 className="h-4 w-4" aria-hidden="true" />
                          <span className="text-xs">Previous</span>
                        </Button>
                        <Button variant="white" size="sm" onClick={() => setPage(p => p + 1)} disabled={!hasMore} aria-label="Next page">
                          <span className="text-xs">Next</span>
                          <IconChevronRightOutline18 className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      </nav>
                    </div>}
                </> : (/* Empty State for Filtered Results */
        <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="mb-4">
                    <div className="bg-ds-neutral-100 dark:bg-ds-neutral-800 mb-4 flex h-16 w-16 items-center justify-center rounded-full">
                      <IconMagnifyingGlass className="text-ds-text-disabled dark:text-ds-text-subtle h-8 w-8" />
                    </div>
                  </div>
                  <h3 className="text-ds-text-primary mb-2 text-lg font-semibold">No connectors match your filters</h3>
                  <p className="body-sm mb-6">Try adjusting your search or filters</p>
                  <button onClick={handleClearAvailableFilters} className="border-ds-border-default text-ds-text-secondary dark:hover:bg-ds-neutral-700 bg-ds-surface-card shadow-ds-soft hover:bg-ds-surface-hover rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors">
                    Clear filters
                  </button>
                </div>)}
            </>) : (/* Configured Connectors List */
      <>
              {filteredConnectors.length > 0 ? <>
                  {hasActiveConfiguredFilters && <div className="mb-4 flex items-center justify-end">
                      <button onClick={handleClearConfiguredFilters} className="text-sm font-medium text-blue-600 transition-colors hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
                        Clear search
                      </button>
                    </div>}

                  <Accordion type="multiple" className="border-ds-border dark:border-ds-neutral-800 border-b">
                    {configuredSections.map(section => <AccordionItem key={section.type} value={section.type} className="border-ds-border dark:border-ds-neutral-800">
                        <AccordionTrigger className="py-4 hover:no-underline">
                          <div className="flex min-w-0 items-center gap-4">
                            <div className="ds-logo-wrapper">
                              <ConnectorLogo type={section.type} name={section.label} logoUrl={section.connectorType?.logoUrl} size="base" />
                            </div>
                            <div className="flex min-w-0 flex-col gap-0.5">
                              <span className="text-ds-text-secondary truncate font-mono text-sm font-medium uppercase">
                                {section.label}
                              </span>
                              <span className="text-ds-text-muted text-xs">
                                {section.count} connector{section.count !== 1 ? "s" : ""}
                              </span>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pb-0">
                          <div className="pb-6">
                            {section.connectors.map((connector, index) => <ConfiguredConnectorRow key={connector.id} connector={connector} connectorType={section.connectorType} isDeleting={deletingId === connector.id} onEdit={handleEdit} onDelete={handleDelete} hideTypeBadge={true} variant="plain" isFirstInGroup={index === 0} isLastInGroup={index === section.connectors.length - 1} />)}
                          </div>
                        </AccordionContent>
                      </AccordionItem>)}
                  </Accordion>
                </> : (/* Empty State for Configured Connectors */
        <>
                  {hasActiveConfiguredFilters ? <div className="flex flex-col items-center justify-center py-16 text-center">
                      <div className="mb-4">
                        <div className="bg-ds-neutral-100 dark:bg-ds-neutral-800 mb-4 flex h-16 w-16 items-center justify-center rounded-full">
                          <IconMagnifyingGlass className="text-ds-text-disabled dark:text-ds-text-subtle h-8 w-8" />
                        </div>
                      </div>
                      <h3 className="text-ds-text-primary mb-2 text-lg font-semibold">
                        No connectors match your filters
                      </h3>
                      <p className="body-sm mb-6">Try a different search or jump back to all connector types.</p>
                      <button onClick={handleClearConfiguredFilters} className="border-ds-border-default text-ds-text-secondary dark:hover:bg-ds-neutral-700 bg-ds-surface-card shadow-ds-soft hover:bg-ds-surface-hover rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors">
                        Reset view
                      </button>
                    </div> : <div className="flex flex-col items-center justify-center py-16 text-center">
                      <EmptyState title="Setup your first connector" description="Connect to external services and APIs to power your workflows." icons={[<ExchangeIcon key="binance" name="binance" size={32} variant="branded" />, <WalletIcon key="metamask" size={32} id="metamask" variant="branded" />, <ExchangeIcon key="coinbase" name="coinbase" size={32} variant="branded" />]} variant="subtle" className="-mt-4 pb-0" size="lg" />
                      <Button onClick={() => setActiveTab("available")}>
                        <span className="text-xs">Browse Available Connectors</span>
                      </Button>
                    </div>}
                </>)}
            </>)}
        </div>}
      {/* Modals */}
      {creatingConnectorType && <ConnectorFormModal isOpen={true} onClose={handleCloseModal} connectorTypes={connectorTypes} preselectedType={creatingConnectorType} />}
      {editingConnector && <ConnectorFormModal isOpen={true} onClose={handleCloseModal} connectorTypes={connectorTypes} connector={editingConnector} />}
      {/* Delete Confirmation Modal */}
      <ConfirmModal open={connectorToDelete !== null} onOpenChange={open => !open && setConnectorToDelete(null)} title="Delete connector?" description="This will remove the connector. Workflows using this connector may stop working." confirmText="Delete" variant="destructive" onConfirm={confirmDelete} />
      {/* Pipedream Post-OAuth Modal */}
      {pendingConnection && <PipedreamConnectorModal pendingConnection={pendingConnection} onClose={clearPendingConnection} />}
    </PageLayout>;
}
export default function ConnectorsPageClient() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Suspense fallback={<ConnectorsListSkeleton count={6} />}><ConnectorsPageContent /></Suspense>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}